import { log2 } from "./utils/log2";
import { sendSocketMessage, socketMessageStream } from "./utils/socketStream";
import store from "./store";
import _ from "lodash";
import { incomingChatSound, newConversationSound, newMessageSound } from "./utils/notificationSounds";
import SYSTEM_PUBSUB, { systemMessageStream, SystemMessageTypes } from "./utils/systemPubSub";
import { filter, first, map, timeout } from "rxjs/operators";
import router from "./router";
import { message } from "ant-design-vue";
import { i18n } from "./language";

let log = log2("ChatWatcher");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
let singletonInstance = null;

const COMMANDS = {
    WATCH_CONTACTS: "user watch-contacts",
    USER_START_CHAT: "user start-chat",
};

const RESPONSE_COMMANDS = {
    CONVERSATION_CLOSED: "conversation_destroyed",
    NEW_CHAT_STARTED: "new_chat_started",
    CONVERSATION_ASSIGNED: "conversation_add",
    MESSAGE: "message",
    VIEWED_CONTACT: "viewed_contact",
    AGENT_JOINED_CHAT: "agent_added_to_chat",
    AGENT_LEAVE_CHAT: "agent_leave_from_chat",
    VISITOR_UPDATED: "visitor_updated",
    JOIN_CHAT_IMMEDIATELY: "join_chat_immediately",
    CHAT_AGENTS_CHANGED: "chat_agents_changed",
};

const retryFetch = (fn, retryCount, maxRetryCount = 0, laterTry) => {
    if (retryCount >= maxRetryCount) return;
    return fn().catch((err) => {
        log.debug("retryFetch", { err, retryCount });
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                retryFetch(fn, retryCount + 1, maxRetryCount, laterTry)
                    .then(resolve)
                    .catch(reject);
            }, laterTry * 1000);
        });
    });
};

const fetchContactList = (concat = false, retryCount = 1) => {
    if (!concat) {
        store.dispatch("chat/setContactList", []);
        store.dispatch("chat/setContacts", []);
    }
    const fn = () => store.dispatch("chat/getConversationList");
    store.dispatch("chat/setContactLoading", true);
    retryFetch(fn, 0, retryCount, 3).then((res) => {
        const items = res.data;
        if (concat) {
            const oldItems = store.state.chat.contactLists;
            const contacts = [...oldItems, ...items];
            store.dispatch("chat/setContactList", contacts);
        } else {
            store.dispatch("chat/setContactList", items);
        }
        store.dispatch("chat/setContactLoading", false);
    });
};

/**
 * Default filter modda stats'lar contactList üzerinden hesaplanır
 */
const getContactStats = () => {
    const isDefaultFilter =
        store.state.chat.contactListFilterMode === "custom" &&
        ["all-agent-inbox", null].includes(store.state.chat.selectedCustomFilterTemplate);
    if (isDefaultFilter) {
        log.debug("getContactStats disabled in default filter mode");
        return;
    }
    store.dispatch("chat/getContactListStats");
};

let contactListTimerId = null;
let statTimerId = null;
const fetchContactListDebounced = (concat = false, retryCount = 1, delay = 500) => {
    if (contactListTimerId) clearTimeout(contactListTimerId);
    if (delay === 0) return fetchContactList(concat, retryCount);
    else {
        contactListTimerId = setTimeout(() => {
            fetchContactList(concat, retryCount);
        }, delay);
    }
};
const fetchStatsDebounced = (delay = 500) => {
    if (statTimerId) clearTimeout(statTimerId);
    if (delay === 0) return getContactStats();
    else {
        statTimerId = setTimeout(() => {
            getContactStats();
        }, delay);
    }
};

let lastPlayTime = {};
const playSound = (type) => {
    log.debug("playSound", type);
    const userInteracted = store.state.app.userInteracted;
    if (!userInteracted) return;
    if (lastPlayTime[type]) clearTimeout(lastPlayTime[type]);
    const sounds = {
        [RESPONSE_COMMANDS.MESSAGE]: newMessageSound(),
        [RESPONSE_COMMANDS.NEW_CHAT_STARTED]: newConversationSound(),
        [RESPONSE_COMMANDS.CONVERSATION_ASSIGNED]: incomingChatSound(),
    };
    const sound = sounds[type];
    lastPlayTime[type] = setTimeout(() => {
        if (sound) sound.play();
    }, 1000);
};

export default class ChatWatcher {
    constructor() {
        this.listeners = [];
        this.inited = false;
        this.tabVisible = true;
        this.lastTabHiddenTime = null;
        this.totalUnreadCount = 0;
        this.init();
        fetchStatsDebounced(0);
        fetchContactList(false, 50);

        setInterval(async () => {
            const route = router.currentRoute;
            const isChatRoute = route.name === "chat";
            const isTabVisible = store.state.app.tabVisible;
            if (isChatRoute && isTabVisible) fetchStatsDebounced(0);
        }, 30 * 1000);
    }

    static create() {
        if (!singletonInstance) singletonInstance = new ChatWatcher();
        return singletonInstance;
    }
    updateLocalStats() {
        const stats = store.state.chat.stats;
        const isDefaultFilter = store.getters["chat/isDefaultFilter"];
        if (isDefaultFilter) {
            const { free, waiting, late, total, totalActive, chatbot, usersChatCounts } = stats;
            const contactList = store.state.chat.contactLists;
            const newStats = {
                all: totalActive,
                unread: contactList.filter((c) => c.unreadedCount > 0).length,
            };
            store.dispatch("chat/setContactListStats", newStats);
        }
    }

    init() {
        this.inited = true;

        store.watch(
            (state) => state.chat.stats,
            (stats) => {
                if (stats) {
                    this.updateLocalStats();
                }
            }
        );

        store.watch(
            (state) => state.socketStatus.socket_connected,
            (socketConnected) => {
                if (socketConnected) {
                    this.watchContactConversations();
                    const connectionCount = store.state.socketStatus.connection_count;
                    if (connectionCount > 0) this.reSync();
                }
            }
        );
        store.watch(
            (state) => state.chat.contactLists,
            () => {
                const activeContact = store.state.chat.activeContact;
                if (!activeContact) return;
                const contact = store.state.chat.contactLists.find((c) => c._id === activeContact._id);
                if (contact) {
                    store.dispatch("chat/setActiveContact", contact);
                }
                this.updateLocalStats();
            },
            { deep: true }
        );
        store.watch(
            (state) => state.chat.activeContact,
            () => {
                const activeContact = store.state.chat.activeContact;
                const showUnread = store.state.chat.contactListFilters.showUnread;
                if (!activeContact) return;
                const users = activeContact.lastConversation.users;
                const authUser = store.state.auth.user;
                const isWatching = store.state.chat.watchingContactIds.includes(activeContact._id);
                if (users.includes(authUser._id) && isWatching) {
                    store.dispatch("chat/removeContactWatchList", activeContact);
                }
                if (showUnread || activeContact.unreadedCount > 0) {
                    fetchStatsDebounced(100);
                }
                this.updateLocalStats();
            },
            { deep: true }
        );

        store.watch(
            (state) => state.chat.contactPaginationCursor.offset,
            (value, oldValue) => {
                console.log("contactPaginationCursor.offset change", value, oldValue);
                const isEq = _.isEqual(value, oldValue);
                if (isEq) return;
                fetchContactListDebounced(true, 1, 0);
            }
        );
        store.watch(
            (state) => state.chat.contactListFilters,
            (newValue, oldValue) => {
                this.filtersChanged(newValue, oldValue);
            },
            { deep: true }
        );

        store.watch(
            (state, getters) => getters["chat/getFittedContactListFilters"],
            (newValue, oldValue) => {
                this.filtersChanged(newValue, oldValue);
            },
            { deep: true }
        );

        store.watch(
            (state) => state.chat.contactListFilters.showUnread,
            (newValue, oldValue) => {
                if (newValue === oldValue) return;
                fetchContactListDebounced(false, 1, 0);
                fetchStatsDebounced(0);
            },
            { deep: true }
        );
        store.watch(
            (state) => state.chat.contactListFilterMode,
            (newValue, oldValue) => {
                if (newValue === oldValue) return;
                fetchContactListDebounced(false, 1, 0);
                fetchStatsDebounced(0);
            },
            { deep: true }
        );

        store.watch(
            (state) => state.app.tabVisible,
            (visible) => {
                if (visible) {
                    fetchStatsDebounced(0);
                    if (this.lastTabHiddenTime) {
                        const tookTime = Date.now() - this.lastTabHiddenTime;
                        if (tookTime > 1000 * 60 * 5) {
                            fetchContactListDebounced(false, 3, 0);
                        }
                    }
                } else {
                    this.lastTabHiddenTime = Date.now();
                }
            },
            { immediate: true }
        );

        this.initKeyListeners();
        this.initListeners();
        log.debug("ChatWatcher inited.");
    }

    filtersChanged(newValue, oldValue) {
        const newVal = Object.keys(newValue).reduce((acc, key) => {
            if (!["showUnread"].includes(key)) {
                acc[key] = newValue[key];
            }
            return acc;
        }, {});
        const oldVal = Object.keys(oldValue).reduce((acc, key) => {
            if (!["showUnread"].includes(key)) {
                acc[key] = oldValue[key];
            }
            return acc;
        }, {});
        const isEq = _.isEqual(newVal, oldVal);
        if (isEq) return;
        this.watchContactConversations();
        fetchContactListDebounced(false, 1, 0);
        fetchStatsDebounced(0);
    }

    watchContactConversations(ids) {
        const user = store.state.auth.user;
        const site = store.state.site.site;
        const filters = store.getters["chat/getFittedContactListFilters"];
        sendSocketMessage(COMMANDS.WATCH_CONTACTS, {
            siteId: site._id,
            userId: user._id,
            filters,
            ids,
        });
    }

    initKeyListeners() {
        const escListener = (e) => {
            const route = router.currentRoute;
            const activeContact = store.state.chat.activeContact;
            const isChatRoute = route.name === "chat";
            const isTabChat = !route.query.tabIndex || route.query.tabIndex === "0";
            if (activeContact && isChatRoute && isTabChat && e.key === "Escape") {
                store.dispatch("chat/setActiveContact", null);
            }
        };

        document.addEventListener("keydown", escListener);
        this.listeners.push(() => {
            document.removeEventListener("keydown", escListener);
        });
    }

    initListeners() {
        const socketStream = socketMessageStream.subscribe(({ cmd, data }) => {
            if (cmd === RESPONSE_COMMANDS.CONVERSATION_CLOSED) {
                log.debug("socket.CONVERSATION_CLOSED", data);
                this.conversationClosed(data);
            } else if (cmd === RESPONSE_COMMANDS.NEW_CHAT_STARTED) {
                log.debug("socket.NEW_CHAT_STARTED", data);
                this.newContactConnected(data);
            } else if (cmd === RESPONSE_COMMANDS.CONVERSATION_ASSIGNED) {
                log.debug("socket.CONVERSATION_ASSIGNED", data);
                this.newContactConnected(data, true);
            } else if (cmd === RESPONSE_COMMANDS.MESSAGE) {
                log.debug("socket.MESSAGE", { data });
                const messageTypes = [
                    "visitor",
                    "visitor-media",
                    "visitor-location",
                    "user",
                    "user-media",
                    "user-location",
                ];
                const isMessage = messageTypes.includes(data.messageType);
                if (isMessage && !data.isTeamChat) this.newMessageReceived(data);
            } else if (cmd === RESPONSE_COMMANDS.VIEWED_CONTACT) {
                log.debug("socket.VIEWED_CONTACT", { data });
                this.viewedContact(data);
            } else if (cmd === RESPONSE_COMMANDS.AGENT_JOINED_CHAT) {
                log.debug("socket.AGENT_JOINED_CHAT", data);
                this.agentJoinedChat(data);
            } else if (cmd === RESPONSE_COMMANDS.AGENT_LEAVE_CHAT) {
                log.debug("socket.AGENT_LEAVE_CHAT", data);
                this.agentLeaveChat(data);
            } else if (cmd === RESPONSE_COMMANDS.VISITOR_UPDATED) {
                log.debug("socket.VISITOR_UPDATED", data);
                this.visitorUpdated(data);
            } else if (cmd === RESPONSE_COMMANDS.JOIN_CHAT_IMMEDIATELY) {
                log.debug("socket.JOIN_CHAT_IMMEDIATELY", data);
                console.log("JOIN_CHAT_IMMEDIATELY", data);
                this.joinChatImmediately(data);
            } else if (cmd === RESPONSE_COMMANDS.CHAT_AGENTS_CHANGED) {
                console.log("socket.CHAT_AGENTS_CHANGED", data);
                this.chatAgentsChanged(data);
            }
        });

        const tabVisibilitySub = systemMessageStream
            .pipe(
                filter((x) => x.type === SystemMessageTypes.TAB_VISIBILITY_CHANGE),
                map((x) => x.payload)
            )
            .subscribe((payload) => {
                const { visible } = payload;
                this.tabVisibleAction(visible);
            });

        this.listeners.push(() => {
            socketStream.unsubscribe();
            tabVisibilitySub.unsubscribe();
        });
    }

    tabVisibleAction(visible) {
        if (visible) {
            this.totalUnreadCount = 0;
        } else {
            document.title = this.totalUnreadCount > 0 ? `(${this.totalUnreadCount} new message) Supsis` : "Supsis";
        }
        this.tabVisible = visible;
    }

    async newMessageReceived(message) {
        const isDefaultFilter =
            store.state.chat.contactListFilterMode === "custom" &&
            ["all-agent-inbox", null].includes(store.state.chat.selectedCustomFilterTemplate);
        log.debug("newMessageReceived", { message, isDefaultFilter });
        const unreadContactCount = store.state.chat.contactLists.filter((c) => c.unreadedCount > 0).length;
        const unreadStat = store.state.chat.contactPaginationCursor.stats.unread;
        if (unreadContactCount > unreadStat) {
            fetchStatsDebounced(100);
        } else {
            fetchStatsDebounced(3 * 1000);
        }
        if (!message.visitorId) {
            log.warn("newMessageReceived -> visitorId not exists", [message]);
            return;
        }

        let contact = store.state.chat.contactLists.find((c) => c._id === message.visitorId);
        if (!contact && !isDefaultFilter) {
            // todo burada hata var
            // if (!contact) {
            contact = await store.dispatch("chat/getContactById", {
                contactId: message.visitorId,
                cache: true,
                ignoreSource: "newMessageReceived",
            });
        }
        if (!contact) {
            log.debug("newMessageReceived -> Contact not exists", {
                message,
            });
            return;
        }

        const { uniqMessageId, message: description, asset, location, messageType, createdDate } = message;
        store.commit("chat/ACT_PENDING_MESSAGE", uniqMessageId);

        if ((messageType || "").startsWith("visitor")) {
            const user = store.state.auth.user;
            const activeContact = store.state.chat.activeContact;
            const isActiveContact = activeContact?._id === message.visitorId;
            const playRequired =
                contact.lastConversation.users.includes(user._id) &&
                ((isActiveContact && !this.tabVisible) || !isActiveContact);
            if (playRequired) playSound(RESPONSE_COMMANDS.MESSAGE);
            if (!this.tabVisible) this.totalUnreadCount += 1;
        }

        const isAlreadyExits = store.state.chat.contactLists.find((c) => c._id === message.visitorId);
        if (!isAlreadyExits) this.addContactList(contact, "newMessageReceived");

        this.updateContactMultipleKey(message.visitorId, undefined, {
            lastInteractionDate: createdDate,
            lastMessage: {
                createdDate: createdDate,
                _id: uniqMessageId,
                description,
                asset,
                location,
                type: messageType,
            },
        });
        store.dispatch("chat/incContactUnreadCount", {
            contactId: message.visitorId,
        });
    }

    viewedContact(data) {
        log.debug("viewedContact", {});

        this.updateContactMultipleKey(data.contactId, undefined, {
            unreadedCount: 0,
        });
    }

    removeContactListIfNeeded(contactId, ignoreSource) {
        log.debug("contact.removeContactListIfNeeded called" + contactId + " source:" + ignoreSource);
        const contact = store.state.chat.contactLists.find((c) => c._id === contactId);
        if (contact) {
            const filters = store.getters["chat/getFittedContactListFilters"];
            const lastChatStatusList = filters.lastChatStatusList || [];
            const filterOwners = filters.lastChatOwner || [];
            const lastStatus = _.get(contact, "lastConversation.status");
            const chatOwners = _.get(contact, "lastConversation.users", []) || [];
            if (lastChatStatusList.length === 0) return;
            const isStatusNotMatched = !!lastStatus
                ? !lastChatStatusList.includes(null) && lastChatStatusList.every((s) => s !== lastStatus)
                : false;
            const isOwnerMatched =
                chatOwners.some((userId) => {
                    return filterOwners.map((x) => x.value).includes(userId);
                }) ||
                (filterOwners.map((x) => x.value).includes("none") && chatOwners.length === 0) ||
                filterOwners.length === 0;
            log.debug("contact.removeContactListIfNeeded " + contactId + " source:" + ignoreSource, {
                isStatusNotMatched,
                isOwnerMatched,
                lastChatStatusList,
                lastStatus,
                ownerCheck: {
                    filterOwners,
                    chatOwners,
                    isOwnerMatched,
                },
            });
            if (isStatusNotMatched || !isOwnerMatched) {
                this.removeContactList(contact, ignoreSource);
                fetchStatsDebounced(100);
            }
        }
    }

    updateContactMultipleKey(contactId, conversationId, set) {
        log.debug(`updateContactMultipleKey contact=${contactId} chat=${conversationId}`, set);
        const contact = store.state.chat.contactLists.find(
            (c) => c._id === contactId || c.lastConversation?._id === conversationId
        );
        if (contact) {
            store.dispatch("chat/updateMultipleKeyContact", {
                contactId,
                conversationId,
                set,
            });
            this.removeContactListIfNeeded(contact._id, "updateContactMultipleKey");
        }
    }

    async agentJoinedChat(data) {
        log.debug("agentJoinedChat", data);
        let contact = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        if (!contact)
            contact = await store.dispatch("chat/getContactById", {
                contactId: data.contactId,
                ignoreSource: "agentJoinedChat",
            });
        if (!contact) {
            log.error("Not should be here. agentJoinedChat -> Contact not exists", {});
            return;
        }
        const users = contact.lastConversation.users.concat(data.userId);
        contact.lastConversation.users = _.uniq([...contact.lastConversation.users, data.userId]);
        const isAlreadyExits = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        const isShowingContact = await store.dispatch("chat/isShowingContactList", data.contactId);
        if (!isAlreadyExits && isShowingContact) {
            this.addContactList(contact, "agentJoinedChat");
        }

        this.updateContactMultipleKey(data.contactId, undefined, {
            lastConversation: {
                ...contact.lastConversation,
                users: _.uniq(users),
                status: "active",
            },
        });
        const status = store.state.chat.contactLists.find((c) => c._id === data.contactId)?.lastConversation?.status;
        log.debug(
            `contact. agentJoinedChat after update -> ${data.contactId} status: ${status} old=${contact.lastConversation.status}`
        );

        this.removeContactListIfNeeded(contact._id, "agentJoinedChat");
        if (data.userId === store.state.auth.user._id && (data.isAutoAssign || data.forward)) {
            playSound(RESPONSE_COMMANDS.CONVERSATION_ASSIGNED);
        }
    }

    async agentLeaveChat(data) {
        log.debug("agentLeaveChat", data);
        let contact = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        if (!contact)
            contact = await store.dispatch("chat/getContactById", {
                contactId: data.contactId,
                ignoreSource: "agentLeaveChat",
            });
        if (!contact) {
            log.error("Not should be here. agentJoinedChat -> Contact not exists", {});
            return;
        }
        const users = contact.lastConversation.users;
        const filteredUsers = users.filter((u) => u !== data.userId);
        contact.lastConversation.users = filteredUsers;

        this.updateContactMultipleKey(undefined, data.conversationId, {
            lastConversation: {
                ...contact.lastConversation,
                users: _.uniq(filteredUsers),
            },
        });

        const isAlreadyExits = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        if (!isAlreadyExits) this.addContactList(contact, "agentLeaveChat");
    }

    async conversationClosed(data) {
        log.debug("conversationClosed", data);
        const contact = store.state.chat.contactLists.find((c) => c.lastConversation._id === data.conversationId);
        const activeContact = store.state.chat.activeContact;
        if (!contact) {
            log.debug("conversationClosed -> Contact not exists", {
                data,
            });
            return;
        }
        store.dispatch("chat/unWatchContactConversation", contact);

        this.updateContactMultipleKey(undefined, data.conversationId, {
            lastConversation: {
                ...contact.lastConversation,
                status: "destroyed",
            },
        });

        if (activeContact && activeContact._id === contact._id) {
            store.dispatch("chat/setActiveContact", null);
        }

        this.removeContactListIfNeeded(contact._id, "conversationClosed");
    }

    async newContactConnected(data, refresh = false) {
        const lastAgentStartedChatId = store.state.chat.lastAgentStartedChatId;
        if (lastAgentStartedChatId?.contactId === data.visitor._id) {
            SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.APPLY_FILTER_TEMPLATE, "all-agent-inbox");
        }

        const isShowingContactList = await store.dispatch("chat/isShowingContactList", data.visitor._id);

        log.debug("newContactConnected " + data.visitor._id, data, "isShowingContactList=" + isShowingContactList);
        if (isShowingContactList) {
            playSound(RESPONSE_COMMANDS.NEW_CHAT_STARTED);
            let contact = store.state.chat.contactLists.find((c) => c._id === data._id);
            if (!contact || refresh) {
                contact = await store.dispatch("chat/getContactById", {
                    contactId: data.visitor._id,
                    cache: false,
                    ignoreSource: "newContactConnected",
                });
            }

            if (contact) {
                this.updateContactMultipleKey(contact._id, undefined, {
                    lastConversation: contact.lastConversation,
                    inited: true,
                });
            }
            this.addContactList(
                {
                    ...contact,
                    lastInteractionDate: new Date().toISOString(),
                },
                "newContactConnected"
            );
            this.watchContactConversations([data._id]);
            if (lastAgentStartedChatId?.contactId === data.visitor._id) {
                store.dispatch("chat/setActiveContact", contact);
            }
        }
    }

    async chatAgentsChanged({ contactId, conversationId }) {
        const isShowingContactList = await store.dispatch("chat/isShowingContactList", contactId);

        log.debug("chatAgentsChanged " + contactId, "isShowingContactList=" + isShowingContactList);
        if (isShowingContactList) {
            // playSound(RESPONSE_COMMANDS.NEW_CHAT_STARTED);

            let contact = await store.dispatch("chat/getContactById", {
                contactId,
                cache: false,
                ignoreSource: "chatAgentsChanged",
            });

            if (contact) {
                this.updateContactMultipleKey(contact._id, undefined, {
                    lastConversation: contact.lastConversation,
                    inited: true,
                });
            }
            this.addContactList(
                {
                    ...contact,
                    lastInteractionDate: new Date().toISOString(),
                },
                "newContactConnected"
            );
            this.watchContactConversations([conversationId]);
        }
    }

    addContactList(contact, ignoreSource) {
        log.debug("contact.add", contact._id, " source=" + ignoreSource, contact);
        const length = store.state.chat.contactLists.length + 1;
        const limit = store.state.chat.contactPaginationCursor.limit;
        const isDeleteRequired = length > limit;
        store.dispatch("chat/setContactList", [contact, ...store.state.chat.contactLists]);
        if (isDeleteRequired) {
            const lastItem = store.state.chat.contactLists[store.state.chat.contactLists.length - 1];
            const activeContact = store.state.chat.activeContact;
            if (lastItem && lastItem._id !== activeContact?._id) this.removeContactList(lastItem);
        }
        fetchStatsDebounced(100);
        if (store.state.chat.activeContact?._id === contact._id) {
            store.dispatch("chat/setActiveContact", contact);
        }
        //this.removeContactListIfNeeded(contact._id, ignoreSource);
    }

    removeContactList(contact, ignoreSource) {
        log.debug(`contact.remove ${contact._id} source=${ignoreSource}`);
        store.dispatch(
            "chat/setContactList",
            store.state.chat.contactLists.filter((c) => c._id !== contact._id)
        );
        // store.dispatch(
        //     "chat/setContacts",
        //     store.state.chat.contacts.filter((c) => c._id !== contact._id)
        // );
        if (store.state.chat.activeContact?._id === contact._id) {
            store.dispatch("chat/setActiveContact", null);
        }
        store.dispatch("chat/unWatchContactConversation", contact);
    }

    visitorUpdated(data) {
        log.debug("visitorUpdated", data);
        const userId = data.userId;
        const authUser = store.state.auth.user;
        if (userId === authUser._id) {
            log.debug("VisitorUpdated event not processed! This event sended from me!");
            return;
        }
        const contact = store.state.chat.contactLists.find((c) => c._id === data.visitor._id);
        if (!contact) {
            log.debug("visitorUpdated -> Contact not exists", {
                data,
            });
            return;
        }

        const unExceptedFields = ["lastConversation", "inited", "lastMessage", "lastInteractionDate"];
        const updatedFields = Object.keys(contact)
            .filter((key) => !unExceptedFields.includes(key))
            .reduce((acc, key) => {
                if (data.visitor[key] !== undefined && contact[key] !== data.visitor[key]) {
                    if (key === "tags") {
                        data.visitor.tags = data.visitor.tags.map((tag) => tag._id);
                    } else if (key === "fullname") {
                        data.visitor.fullname = data.visitor.fullname || contact.lastConversation.fullname;
                    } else if (key === "email") {
                        data.visitor.email = data.visitor.email || contact.lastConversation.email;
                    }
                    acc[key] = data.visitor[key];
                }
                return acc;
            }, {});

        this.updateContactMultipleKey(data.visitor._id, undefined, updatedFields);
    }

    joinLostChats() {
        log.debug("joinLostChats", {});
        const authUser = store.state.auth.user;
        const site = store.state.site.site;
        const myChatIds = store.state.chat.myChatIds;
        if (myChatIds.length > 0) {
            sendSocketMessage("user join-lost-conversations", {
                siteId: site._id,
                userId: authUser._id,
                ids: myChatIds,
                observing: false,
            });
        }
    }

    static startChatImmediately(data) {
        log.debug("startChatImmediately", data);
        const site = store.state.site.site;
        const user = store.state.auth.user;
        const hideMessage = message.loading(i18n.t("wp.chatStartingWait"), 0);
        sendSocketMessage(COMMANDS.USER_START_CHAT, {
            siteId: site._id,
            userId: user._id,
            ...data,
        });

        socketMessageStream
            .pipe(
                filter((x) => x.cmd === RESPONSE_COMMANDS.JOIN_CHAT_IMMEDIATELY),
                timeout(5000),
                first()
            )
            .toPromise()
            .then(async (x) => {
                message.success(i18n.t("wp.chatStarted"));
            })
            .catch((err) => {
                message.error(i18n.t("wp.chatNotStarted"));
            })
            .finally(() => {
                hideMessage();
            });
    }

    async joinChatImmediately(data) {
        log.debug("joinChatImmediately", data);
        router.push("/chat");
        const { contactId, conversationId } = data;
        store.dispatch("chat/setLastAgentStartedContact", { contactId, _id: contactId, conversationId });
        const user = store.state.auth.user;
        const site = store.state.site.site;
        let contact = store.state.chat.contactLists.find((c) => c._id === contactId);
        if (!contact)
            contact = await store.dispatch("chat/getContactById", { contactId, ignoreSource: "joinChatImmediately" });
        if (!contact) {
            log.error("Not should be here. joinChatImmediately -> Contact not exists", {});
        }
        const isMyChat = (contact.lastConversation?.users || []).includes(user._id);
        const isContactExists = store.state.chat.contactLists.find((c) => c._id === contactId);
        log("joinChatImmediately", {
            data,
            contactInState: !!store.state.chat.contactLists.find((c) => c._id === contactId),
            contact,
            isMyChat,
            isContactExists,
        });

        if (isMyChat) {
            if (!isContactExists) this.addContactList(contact, "joinChatImmediately");
        } else {
            sendSocketMessage("user join-conversation", {
                userId: user._id,
                conversationId,
                siteId: site._id,
                reconnect: false,
                autoAssign: false,
                watchMode: false,
                startedFromAgent: true,
            });
        }

        store.dispatch("chat/setActiveContact", contact);
        SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.OPEN_CHAT, {});
    }

    reSync() {
        log.debug("reSync", {});
        if (!this.inited) return;
        log.debug("ChatWatcher reSync started.");
        this.listeners.forEach((listener) => listener());
        this.initListeners();
        this.joinLostChats();
        fetchContactListDebounced(false, 3, 0);
        fetchStatsDebounced(0);
    }
}
